import React, { useState, useEffect } from 'react';
import PlayerDashboard from './PlayerDashboard';
import { getErrorMessage } from '../utils/api';
import ChildSelectionModal from '../components/ChildSelectionModal';
import { buildApiUrl } from '../config/api';

interface ParentDashboardProps {
  onBackToLanding?: () => void;
}

interface Child {
  player_id: number | null;
  player_name: string;
  relationship: string;
  evaluation: any | null;
  message?: string;
}

// Using buildApiUrl helper instead of API_BASE to ensure correct /api prefix

const ParentDashboard: React.FC<ParentDashboardProps> = ({ onBackToLanding }) => {
  const [children, setChildren] = useState<Child[]>([]);
  const [selectedChild, setSelectedChild] = useState<Child | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showChildSelection, setShowChildSelection] = useState(false);

  useEffect(() => {
    const loadChildren = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Check if playerId is passed in URL (from Parent Portal)
        const urlParams = new URLSearchParams(window.location.search);
        const playerIdParam = urlParams.get('playerId');
        
        const response = await fetch(buildApiUrl('/evaluations/parent/my-children'), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const childrenData = data.children || [];
          setChildren(childrenData);
          
          // If playerId is in URL, select that child
          if (playerIdParam && childrenData.length > 0) {
            const playerIdNum = parseInt(playerIdParam, 10);
            const matchingChild = childrenData.find((child: Child) => child.player_id === playerIdNum);
            if (matchingChild) {
              setSelectedChild(matchingChild);
              setShowChildSelection(false);
              setLoading(false);
              return;
            }
          }
          
          // If multiple children, show selection modal (don't auto-select)
          if (childrenData.length > 1) {
            setShowChildSelection(true);
            setSelectedChild(null);
          } else if (childrenData.length === 1) {
            // Single child - auto-select
            setSelectedChild(childrenData[0]);
            setShowChildSelection(false);
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          setError(getErrorMessage(errorData, 'Failed to load children evaluations'));
        }
      } catch (err) {
        console.error('Error loading children:', err);
        setError('Failed to load children evaluations. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadChildren();
  }, []);

  const handleBackToLogin = () => {
    // Clear login state
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_role');
    localStorage.removeItem('username');
    if (onBackToLanding) {
      onBackToLanding();
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <h1>Parent Dashboard</h1>
          <button onClick={handleBackToLogin} className="back-btn">Exit</button>
        </div>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (children.length === 0) {
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <h1>Parent Dashboard</h1>
          <button onClick={handleBackToLogin} className="back-btn">Exit</button>
        </div>
        <div className="no-evaluations">
          <p>No children linked to your account. Please contact support.</p>
        </div>
      </div>
    );
  }

  // Show child selection modal if multiple children and none selected
  if (showChildSelection && !selectedChild && children.length > 1) {
    return (
      <ChildSelectionModal
        isOpen={true}
        children={children}
        onSelect={(child) => {
          setSelectedChild(child);
          setShowChildSelection(false);
        }}
      />
    );
  }

  // If a child is selected and has an evaluation, show their dashboard
  if (selectedChild && selectedChild.evaluation) {
    // Verify the evaluation matches the selected child
    const evalPlayerName = `${selectedChild.evaluation.player?.first_name || ''} ${selectedChild.evaluation.player?.last_name || ''}`.trim();
    const selectedChildName = selectedChild.player_name.trim();
    
    // Only show dashboard if names match (to prevent showing wrong child's dashboard)
    if (evalPlayerName.toLowerCase() === selectedChildName.toLowerCase() || 
        selectedChild.evaluation.player?.first_name?.toLowerCase() === selectedChildName.split(' ')[0]?.toLowerCase()) {
      return (
        <div>
          <div style={{ padding: '1rem', backgroundColor: '#1f2230', borderBottom: '1px solid #383b48' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: '1650px', margin: '0 auto' }}>
              <div>
                <h2 style={{ margin: 0, color: '#567ad8' }}>Viewing: {selectedChild.player_name}</h2>
                <p style={{ margin: '0.25rem 0 0 0', color: '#797b84', fontSize: '0.9rem' }}>
                  Relationship: {selectedChild.relationship}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {children.length > 1 && (
                  <button
                    onClick={() => {
                      setSelectedChild(null);
                      setShowChildSelection(true);
                    }}
                    className="button ghost"
                  >
                    Switch Child
                  </button>
                )}
                <button onClick={handleBackToLogin} className="button ghost">
                  Exit
                </button>
              </div>
            </div>
          </div>
          <PlayerDashboard
            evaluationData={selectedChild.evaluation}
            onBackToLanding={handleBackToLogin}
          />
        </div>
      );
    } else {
      // Evaluation doesn't match selected child - show error
      return (
        <div className="dashboard">
          <div className="dashboard-header">
            <h1>Parent Dashboard</h1>
            <button onClick={handleBackToLogin} className="back-btn">Exit</button>
          </div>
          <div className="error-message">
            Error: Evaluation data mismatch. Please try selecting the child again.
          </div>
        </div>
      );
    }
  }
  
  // If a child is selected but has no evaluation, show message
  if (selectedChild && !selectedChild.evaluation) {
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <h1>Parent Dashboard</h1>
          <button onClick={handleBackToLogin} className="back-btn">Exit</button>
        </div>
        <div className="dashboard-content">
          <div className="card">
            <h2>{selectedChild.player_name}</h2>
            <p style={{ color: '#797b84', fontSize: '1.1rem', marginTop: '1rem' }}>
              {selectedChild.message || "No evaluation found for this player yet. Your coach will add evaluations here."}
            </p>
            {children.length > 1 && (
              <button
                onClick={() => {
                  setSelectedChild(null);
                  setShowChildSelection(true);
                }}
                className="button primary"
                style={{ marginTop: '1.5rem' }}
              >
                Switch Child
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show child selection if multiple children or no evaluation
  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Parent Dashboard</h1>
        <button onClick={handleBackToLogin} className="back-btn">Exit</button>
      </div>

      <div className="dashboard-content">
        <div className="card">
          <h2>Your Children</h2>
          {children.map((child, index) => (
            <div
              key={index}
              style={{
                padding: '1rem',
                marginBottom: '1rem',
                backgroundColor: '#1f2230',
                borderRadius: '8px',
                border: '1px solid #383b48',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onClick={() => setSelectedChild(child)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1a1b26';
                e.currentTarget.style.borderColor = '#567ad8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#1f2230';
                e.currentTarget.style.borderColor = '#383b48';
              }}
            >
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#567ad8' }}>
                {child.player_name}
              </h3>
              <p style={{ margin: '0.25rem 0', color: '#797b84', fontSize: '0.9rem' }}>
                Relationship: {child.relationship}
              </p>
              {child.evaluation ? (
                <p style={{ margin: '0.5rem 0 0 0', color: '#4ade80', fontSize: '0.9rem' }}>
                  ✓ Has evaluation - Click to view
                </p>
              ) : (
                <p style={{ margin: '0.5rem 0 0 0', color: '#797b84', fontSize: '0.9rem' }}>
                  {child.message || 'No evaluation available yet'}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ParentDashboard;
